import { useState, useEffect } from "react";

// ── Google Fonts ──────────────────────────────────────────────────────────────
const FontLoader = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Jost:wght@200;300;400;500&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --green:    #0F2A24;
      --black:    #111111;
      --offwhite: #F5F3EE;
      --stone:    #D8D2C8;
      --gold:     #C2A46D;
      --serif:    'Cormorant Garamond', Georgia, serif;
      --sans:     'Jost', Helvetica, sans-serif;
    }

    html { scroll-behavior: smooth; }
    body { background: var(--offwhite); color: var(--black); font-family: var(--sans); }

    ::selection { background: var(--gold); color: var(--offwhite); }

    /* scrollbar */
    ::-webkit-scrollbar { width: 4px; }
    ::-webkit-scrollbar-track { background: var(--offwhite); }
    ::-webkit-scrollbar-thumb { background: var(--gold); }
  `}</style>
);

// ── Shared styles (injected once) ─────────────────────────────────────────────
const GlobalStyles = () => (
  <style>{`
    /* NAV */
    .nav {
      position: fixed; top: 0; left: 0; right: 0; z-index: 100;
      display: flex; align-items: center; justify-content: space-between;
      padding: 28px 48px;
      transition: background .4s ease, padding .4s ease, border-color .4s ease;
      border-bottom: 1px solid transparent;
    }
    .nav.scrolled {
      background: rgba(15,42,36,.96);
      backdrop-filter: blur(12px);
      padding: 18px 48px;
      border-bottom-color: rgba(194,164,109,.18);
    }
    .nav-wordmark {
      font-family: var(--serif); font-size: 20px; font-weight: 400; letter-spacing: .12em;
      color: var(--offwhite); text-transform: uppercase; cursor: pointer;
      white-space: nowrap;
    }
    .nav-links { display: flex; gap: 36px; list-style: none; }
    .nav-links a {
      font-family: var(--sans); font-size: 11px; letter-spacing: .18em; text-transform: uppercase;
      color: rgba(245,243,238,.75); text-decoration: none;
      transition: color .2s;
    }
    .nav-links a:hover { color: var(--gold); }
    .nav-utils { display: flex; gap: 20px; align-items: center; }
    .nav-icon {
      background: none; border: none; color: rgba(245,243,238,.75);
      cursor: pointer; transition: color .2s; padding: 4px;
    }
    .nav-icon:hover { color: var(--gold); }
    .nav-cart-badge {
      position: absolute; top: -4px; right: -4px;
      background: var(--gold); color: var(--offwhite);
      font-size: 9px; width: 14px; height: 14px;
      border-radius: 50%; display: flex; align-items: center; justify-content: center;
    }

    /* HERO */
    .hero {
      position: relative; height: 100vh; min-height: 640px;
      display: flex; flex-direction: column; align-items: center; justify-content: center;
      text-align: center; overflow: hidden;
    }
    .hero-bg {
      position: absolute; inset: 0;
      background: linear-gradient(160deg, #0a1f1a 0%, #0F2A24 40%, #1a3a2e 70%, #0d2218 100%);
    }
    .hero-overlay {
      position: absolute; inset: 0;
      background: radial-gradient(ellipse at 50% 60%, rgba(15,42,36,0) 30%, rgba(5,12,10,.7) 100%);
    }
    /* decorative mist lines */
    .hero-mist {
      position: absolute; inset: 0; overflow: hidden; pointer-events: none;
    }
    .hero-mist::before {
      content: '';
      position: absolute; bottom: 0; left: 0; right: 0; height: 45%;
      background: linear-gradient(to top, rgba(15,42,36,.6) 0%, transparent 100%);
    }
    .hero-content { position: relative; z-index: 2; max-width: 720px; padding: 0 24px; }
    .hero-eyebrow {
      font-family: var(--sans); font-size: 10px; letter-spacing: .3em; text-transform: uppercase;
      color: var(--gold); margin-bottom: 24px;
      opacity: 0; animation: fadeUp .8s .3s forwards;
    }
    .hero-title {
      font-family: var(--serif); font-size: clamp(52px, 9vw, 96px);
      font-weight: 300; line-height: 1.05; color: var(--offwhite);
      letter-spacing: .02em; margin-bottom: 12px;
      opacity: 0; animation: fadeUp .9s .5s forwards;
    }
    .hero-title em { font-style: italic; color: var(--gold); }
    .hero-subtitle {
      font-family: var(--serif); font-size: clamp(18px, 2.5vw, 24px);
      font-weight: 300; color: rgba(245,243,238,.7); font-style: italic;
      margin-bottom: 16px;
      opacity: 0; animation: fadeUp .9s .7s forwards;
    }
    .hero-sub2 {
      font-family: var(--sans); font-size: 12px; letter-spacing: .12em;
      color: rgba(245,243,238,.45); margin-bottom: 52px;
      opacity: 0; animation: fadeUp .9s .85s forwards;
    }
    .hero-ctas { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap;
      opacity: 0; animation: fadeUp .9s 1s forwards; }
    .btn-primary {
      font-family: var(--sans); font-size: 11px; letter-spacing: .2em; text-transform: uppercase;
      color: var(--black); background: var(--gold); border: none;
      padding: 16px 36px; cursor: pointer; transition: background .2s, transform .2s;
    }
    .btn-primary:hover { background: #d4b87e; transform: translateY(-1px); }
    .btn-outline {
      font-family: var(--sans); font-size: 11px; letter-spacing: .2em; text-transform: uppercase;
      color: var(--offwhite); background: transparent;
      border: 1px solid rgba(245,243,238,.35); padding: 16px 36px;
      cursor: pointer; transition: border-color .2s, color .2s, transform .2s;
    }
    .btn-outline:hover { border-color: var(--gold); color: var(--gold); transform: translateY(-1px); }
    .hero-scroll {
      position: absolute; bottom: 40px; left: 50%; transform: translateX(-50%);
      display: flex; flex-direction: column; align-items: center; gap: 8px;
      opacity: 0; animation: fadeUp .9s 1.3s forwards;
    }
    .hero-scroll span {
      font-family: var(--sans); font-size: 9px; letter-spacing: .25em;
      text-transform: uppercase; color: rgba(245,243,238,.4);
    }
    .scroll-line {
      width: 1px; height: 40px;
      background: linear-gradient(to bottom, var(--gold), transparent);
      animation: scrollLine 2s ease-in-out infinite;
    }
    /* decorative SVG landscape silhouette */
    .hero-mountains {
      position: absolute; bottom: 0; left: 0; right: 0;
      pointer-events: none; z-index: 1;
    }

    /* SECTION COMMONS */
    .section { padding: 120px 48px; }
    .section-centered { text-align: center; max-width: 760px; margin: 0 auto; }
    .label {
      font-family: var(--sans); font-size: 10px; letter-spacing: .3em; text-transform: uppercase;
      color: var(--gold); margin-bottom: 20px; display: block;
    }
    .h2 {
      font-family: var(--serif); font-size: clamp(36px, 5vw, 62px);
      font-weight: 300; line-height: 1.1; color: var(--black);
    }
    .h2-light { color: var(--offwhite); }
    .h2 em { font-style: italic; }
    .body-copy {
      font-family: var(--sans); font-size: 15px; font-weight: 300; line-height: 1.8;
      color: rgba(17,17,17,.65); max-width: 540px; margin: 0 auto;
    }
    .body-copy-light { color: rgba(245,243,238,.6); }
    .divider { width: 40px; height: 1px; background: var(--gold); margin: 28px auto; }

    /* STATEMENT */
    .statement { background: var(--offwhite); padding: 140px 48px; }
    .statement-inner { max-width: 900px; margin: 0 auto; text-align: center; }
    .statement .h2 { font-size: clamp(38px, 5.5vw, 72px); margin-bottom: 28px; }
    .statement .body-copy { font-size: 17px; max-width: 620px; }

    /* PRODUCTS */
    .products-section { background: var(--black); padding: 120px 48px; }
    .products-grid {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
      gap: 2px; max-width: 1300px; margin: 64px auto 0;
    }
    .product-card {
      background: #161616; padding: 48px 36px;
      border: 1px solid rgba(255,255,255,.04);
      cursor: pointer; transition: background .3s, transform .3s;
      display: flex; flex-direction: column;
    }
    .product-card:hover { background: #1e1e1e; transform: translateY(-3px); }
    .product-bottle {
      height: 180px; margin-bottom: 32px;
      display: flex; align-items: center; justify-content: center;
    }
    .product-name {
      font-family: var(--serif); font-size: 24px; font-weight: 400;
      color: var(--offwhite); margin-bottom: 8px;
    }
    .product-desc {
      font-family: var(--sans); font-size: 12px; letter-spacing: .08em;
      color: rgba(245,243,238,.4); line-height: 1.6; margin-bottom: 24px; flex: 1;
    }
    .product-abv {
      font-family: var(--sans); font-size: 10px; letter-spacing: .2em;
      color: var(--gold); text-transform: uppercase; margin-bottom: 20px;
    }
    .product-link {
      font-family: var(--sans); font-size: 10px; letter-spacing: .2em; text-transform: uppercase;
      color: rgba(245,243,238,.5); text-decoration: none;
      display: flex; align-items: center; gap: 8px;
      transition: color .2s;
    }
    .product-link:hover { color: var(--gold); }
    .product-link::after { content: '→'; transition: transform .2s; }
    .product-link:hover::after { transform: translateX(4px); }

    /* PROVENANCE */
    .provenance { background: var(--offwhite); padding: 0; overflow: hidden; }
    .provenance-panel {
      display: grid; grid-template-columns: 1fr 1fr; min-height: 560px;
      border-bottom: 1px solid rgba(17,17,17,.08);
    }
    .provenance-panel:nth-child(even) .provenance-visual { order: -1; }
    .provenance-visual {
      background: var(--green); position: relative; overflow: hidden;
      min-height: 400px; display: flex; align-items: center; justify-content: center;
    }
    .prov-icon { color: rgba(194,164,109,.2); }
    .provenance-copy {
      padding: 80px 72px; display: flex; flex-direction: column; justify-content: center;
    }
    .provenance-copy .h2 { margin-bottom: 20px; }
    .provenance-copy .body-copy { margin: 0; text-align: left; }

    /* MISSION */
    .mission-section {
      background: var(--green); padding: 140px 48px; position: relative; overflow: hidden;
    }
    .mission-section::before {
      content: '';
      position: absolute; top: -50%; left: -10%; right: -10%; bottom: -50%;
      background: radial-gradient(ellipse at 30% 50%, rgba(194,164,109,.06) 0%, transparent 60%);
      pointer-events: none;
    }
    .mission-inner {
      display: grid; grid-template-columns: 1fr 1fr; gap: 80px;
      max-width: 1200px; margin: 0 auto; align-items: center;
    }
    .mission-stats {
      display: grid; grid-template-columns: 1fr 1fr; gap: 1px;
      background: rgba(194,164,109,.15); margin-top: 52px;
    }
    .mission-stat {
      background: rgba(15,42,36,.8); padding: 32px 28px;
    }
    .mission-stat-num {
      font-family: var(--serif); font-size: 36px; color: var(--gold);
      margin-bottom: 6px;
    }
    .mission-stat-label {
      font-family: var(--sans); font-size: 10px; letter-spacing: .15em; text-transform: uppercase;
      color: rgba(245,243,238,.4);
    }
    .mission-visual {
      position: relative; height: 520px;
      background: rgba(0,0,0,.2); overflow: hidden;
      display: flex; align-items: center; justify-content: center;
    }

    /* LEGACY */
    .legacy {
      background: var(--black); padding: 160px 48px; text-align: center; position: relative;
      overflow: hidden;
    }
    .legacy::before {
      content: 'L&L';
      position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%);
      font-family: var(--serif); font-size: 380px; font-weight: 300;
      color: rgba(255,255,255,.02); pointer-events: none; white-space: nowrap;
      line-height: 1;
    }
    .legacy-inner { position: relative; z-index: 2; max-width: 800px; margin: 0 auto; }
    .legacy .h2 { font-size: clamp(44px, 7vw, 88px); margin-bottom: 40px; }
    .legacy-lines {
      font-family: var(--serif); font-size: clamp(18px, 2.2vw, 24px);
      font-style: italic; color: rgba(245,243,238,.45); line-height: 1.9;
      margin-bottom: 48px;
    }
    .legacy-line { display: block; }
    .legacy-statement {
      font-family: var(--sans); font-size: 13px; letter-spacing: .1em;
      color: rgba(245,243,238,.35); max-width: 560px; margin: 0 auto; line-height: 1.8;
    }

    /* JOURNAL */
    .journal-section { background: var(--offwhite); padding: 120px 48px; }
    .journal-grid {
      display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px;
      max-width: 1200px; margin: 64px auto 0;
    }
    .journal-card {
      cursor: pointer; overflow: hidden;
    }
    .journal-card-img {
      height: 260px; overflow: hidden; position: relative;
    }
    .journal-card-img-inner {
      width: 100%; height: 100%;
      background: var(--green);
      display: flex; align-items: center; justify-content: center;
      transition: transform .6s ease;
    }
    .journal-card:hover .journal-card-img-inner { transform: scale(1.04); }
    .journal-card-body { padding: 28px 0; }
    .journal-cat {
      font-family: var(--sans); font-size: 9px; letter-spacing: .25em; text-transform: uppercase;
      color: var(--gold); margin-bottom: 10px; display: block;
    }
    .journal-title {
      font-family: var(--serif); font-size: 22px; font-weight: 400; color: var(--black);
      line-height: 1.3; margin-bottom: 12px;
    }
    .journal-excerpt {
      font-family: var(--sans); font-size: 13px; font-weight: 300;
      color: rgba(17,17,17,.5); line-height: 1.7;
    }

    /* NEWSLETTER */
    .newsletter { background: var(--green); padding: 120px 48px; text-align: center; }
    .newsletter-inner { max-width: 520px; margin: 0 auto; }
    .newsletter .h2 { margin-bottom: 16px; }
    .newsletter .body-copy { margin-bottom: 40px; }
    .newsletter-form { display: flex; gap: 0; max-width: 420px; margin: 0 auto; }
    .newsletter-input {
      flex: 1; background: rgba(245,243,238,.08); border: 1px solid rgba(245,243,238,.15);
      border-right: none; padding: 16px 20px;
      font-family: var(--sans); font-size: 13px; color: var(--offwhite);
      outline: none; transition: border-color .2s;
    }
    .newsletter-input::placeholder { color: rgba(245,243,238,.35); }
    .newsletter-input:focus { border-color: var(--gold); }
    .newsletter-btn {
      background: var(--gold); border: none; padding: 16px 28px;
      font-family: var(--sans); font-size: 10px; letter-spacing: .2em; text-transform: uppercase;
      color: var(--black); cursor: pointer; transition: background .2s;
      white-space: nowrap;
    }
    .newsletter-btn:hover { background: #d4b87e; }

    /* FOOTER */
    .footer { background: var(--black); padding: 80px 48px 40px; }
    .footer-top {
      display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 48px;
      padding-bottom: 64px; border-bottom: 1px solid rgba(255,255,255,.06);
      margin-bottom: 40px;
    }
    .footer-brand-name {
      font-family: var(--serif); font-size: 22px; font-weight: 300; letter-spacing: .1em;
      color: var(--offwhite); text-transform: uppercase; margin-bottom: 16px;
    }
    .footer-tagline {
      font-family: var(--sans); font-size: 12px; font-weight: 300; letter-spacing: .06em;
      color: rgba(245,243,238,.35); line-height: 1.7; max-width: 240px;
    }
    .footer-col-title {
      font-family: var(--sans); font-size: 9px; letter-spacing: .25em; text-transform: uppercase;
      color: var(--gold); margin-bottom: 20px;
    }
    .footer-links { list-style: none; display: flex; flex-direction: column; gap: 10px; }
    .footer-links a {
      font-family: var(--sans); font-size: 13px; font-weight: 300;
      color: rgba(245,243,238,.45); text-decoration: none; transition: color .2s;
    }
    .footer-links a:hover { color: var(--offwhite); }
    .footer-bottom {
      display: flex; align-items: center; justify-content: space-between;
    }
    .footer-copy {
      font-family: var(--sans); font-size: 11px; font-weight: 300; letter-spacing: .06em;
      color: rgba(245,243,238,.25);
    }
    .footer-legal { display: flex; gap: 24px; }
    .footer-legal a {
      font-family: var(--sans); font-size: 11px; color: rgba(245,243,238,.25);
      text-decoration: none; transition: color .2s;
    }
    .footer-legal a:hover { color: rgba(245,243,238,.55); }

    /* SHOP PAGE */
    .shop-hero {
      background: var(--black); padding: 160px 48px 80px;
      text-align: center; border-bottom: 1px solid rgba(255,255,255,.06);
    }
    .shop-filters {
      display: flex; gap: 4px; justify-content: center; margin: 48px 0;
      flex-wrap: wrap;
    }
    .filter-btn {
      font-family: var(--sans); font-size: 10px; letter-spacing: .18em; text-transform: uppercase;
      border: 1px solid rgba(17,17,17,.2); background: transparent; color: rgba(17,17,17,.5);
      padding: 10px 24px; cursor: pointer; transition: all .2s;
    }
    .filter-btn.active, .filter-btn:hover {
      background: var(--black); color: var(--offwhite); border-color: var(--black);
    }
    .shop-grid {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
      gap: 2px; max-width: 1300px; margin: 0 auto;
    }
    .shop-card {
      background: var(--offwhite); padding: 48px 36px; cursor: pointer;
      border: 1px solid rgba(17,17,17,.06); transition: background .25s;
    }
    .shop-card:hover { background: #f0ede6; }
    .shop-card-img {
      height: 220px; display: flex; align-items: center; justify-content: center;
      margin-bottom: 28px;
    }
    .shop-card-name { font-family: var(--serif); font-size: 26px; color: var(--black); margin-bottom: 6px; }
    .shop-card-desc { font-family: var(--sans); font-size: 12px; color: rgba(17,17,17,.45); margin-bottom: 20px; line-height: 1.6; }
    .shop-card-row { display: flex; justify-content: space-between; align-items: center; }
    .shop-card-price { font-family: var(--serif); font-size: 22px; color: var(--black); }
    .btn-add {
      font-family: var(--sans); font-size: 10px; letter-spacing: .18em; text-transform: uppercase;
      background: var(--black); color: var(--offwhite); border: none;
      padding: 12px 24px; cursor: pointer; transition: background .2s;
    }
    .btn-add:hover { background: var(--green); }

    /* PRODUCT PAGE */
    .product-page { max-width: 1300px; margin: 0 auto; padding: 120px 48px 80px; }
    .product-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: start; }
    .product-img-wrap {
      background: var(--black); aspect-ratio: 3/4;
      display: flex; align-items: center; justify-content: center; position: sticky; top: 100px;
    }
    .product-info { padding-top: 12px; }
    .product-page-name { font-family: var(--serif); font-size: clamp(36px,5vw,56px); font-weight: 300; color: var(--black); line-height: 1.1; margin-bottom: 8px; }
    .product-page-abv { font-family: var(--sans); font-size: 11px; letter-spacing: .2em; color: var(--gold); text-transform: uppercase; margin-bottom: 24px; }
    .product-page-price { font-family: var(--serif); font-size: 28px; color: var(--black); margin-bottom: 36px; }
    .btn-atc {
      width: 100%; font-family: var(--sans); font-size: 11px; letter-spacing: .2em; text-transform: uppercase;
      background: var(--black); color: var(--offwhite); border: none;
      padding: 20px; cursor: pointer; transition: background .2s; margin-bottom: 48px;
    }
    .btn-atc:hover { background: var(--green); }
    .product-section { padding: 28px 0; border-top: 1px solid rgba(17,17,17,.1); }
    .product-section-title { font-family: var(--sans); font-size: 10px; letter-spacing: .2em; text-transform: uppercase; color: var(--gold); margin-bottom: 14px; }
    .product-section-body { font-family: var(--sans); font-size: 14px; font-weight: 300; line-height: 1.8; color: rgba(17,17,17,.65); }
    .tasting-notes { display: flex; flex-direction: column; gap: 8px; }
    .tasting-note {
      font-family: var(--sans); font-size: 13px; font-weight: 300; color: rgba(17,17,17,.7);
      padding: 10px 0; border-bottom: 1px solid rgba(17,17,17,.06);
      display: flex; align-items: center; gap: 12px;
    }
    .tasting-note::before { content: '–'; color: var(--gold); }

    /* MISSION PAGE */
    .mission-hero {
      background: var(--green); padding: 180px 48px 120px;
      text-align: center; position: relative; overflow: hidden;
    }
    .mission-body { background: var(--offwhite); }
    .mission-block { max-width: 760px; margin: 0 auto; padding: 100px 48px; }
    .mission-block:not(:last-child) { border-bottom: 1px solid rgba(17,17,17,.08); }
    .mission-block .h2 { margin-bottom: 24px; text-align: center; }
    .mission-block .body-copy { text-align: center; }
    .projects-grid {
      display: grid; grid-template-columns: repeat(2, 1fr); gap: 2px; margin-top: 48px;
    }
    .project-card {
      background: var(--black); padding: 40px 36px;
    }
    .project-name { font-family: var(--serif); font-size: 22px; color: var(--offwhite); margin-bottom: 10px; }
    .project-desc { font-family: var(--sans); font-size: 13px; font-weight: 300; color: rgba(245,243,238,.45); line-height: 1.7; }

    /* CONTACT PAGE */
    .contact-page { max-width: 700px; margin: 0 auto; padding: 180px 48px 120px; }
    .contact-form { display: flex; flex-direction: column; gap: 20px; margin-top: 48px; }
    .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .form-field { display: flex; flex-direction: column; gap: 8px; }
    .form-label { font-family: var(--sans); font-size: 10px; letter-spacing: .2em; text-transform: uppercase; color: rgba(17,17,17,.5); }
    .form-input, .form-textarea {
      background: transparent; border: none; border-bottom: 1px solid rgba(17,17,17,.2);
      padding: 12px 0; font-family: var(--sans); font-size: 14px; font-weight: 300;
      color: var(--black); outline: none; transition: border-color .2s;
    }
    .form-input:focus, .form-textarea:focus { border-bottom-color: var(--gold); }
    .form-textarea { resize: vertical; min-height: 100px; }
    .form-submit {
      font-family: var(--sans); font-size: 11px; letter-spacing: .2em; text-transform: uppercase;
      background: var(--black); color: var(--offwhite); border: none; padding: 20px 48px;
      cursor: pointer; transition: background .2s; align-self: flex-start; margin-top: 12px;
    }
    .form-submit:hover { background: var(--green); }
    .contact-details {
      display: grid; grid-template-columns: repeat(3, 1fr); gap: 2px;
      margin-top: 80px;
    }
    .contact-detail { padding: 36px 0; border-top: 2px solid rgba(17,17,17,.1); }
    .contact-detail-title { font-family: var(--sans); font-size: 10px; letter-spacing: .2em; text-transform: uppercase; color: var(--gold); margin-bottom: 10px; }
    .contact-detail-val { font-family: var(--serif); font-size: 16px; color: var(--black); }

    /* AGE GATE */
    .age-gate {
      position: fixed; inset: 0; z-index: 999;
      background: var(--green); display: flex; align-items: center; justify-content: center;
      transition: opacity .6s ease;
    }
    .age-gate.hidden { opacity: 0; pointer-events: none; }
    .age-gate-inner { text-align: center; max-width: 440px; padding: 48px; }
    .age-gate-title { font-family: var(--serif); font-size: 32px; font-weight: 300; color: var(--offwhite); margin-bottom: 8px; }
    .age-gate-sub { font-family: var(--sans); font-size: 13px; color: rgba(245,243,238,.5); margin-bottom: 40px; line-height: 1.7; }
    .age-gate-btns { display: flex; gap: 12px; justify-content: center; }

    /* ANIMATIONS */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(22px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes scrollLine {
      0%, 100% { transform: scaleY(1); opacity: 1; }
      50%       { transform: scaleY(.4); opacity: .4; }
    }

    /* PAGE TRANSITIONS */
    .page { animation: fadeUp .5s ease; }

    /* RESPONSIVE */
    @media (max-width: 900px) {
      .nav { padding: 20px 24px; }
      .nav.scrolled { padding: 14px 24px; }
      .nav-links { display: none; }
      .section { padding: 80px 24px; }
      .statement { padding: 80px 24px; }
      .provenance-panel { grid-template-columns: 1fr; }
      .provenance-panel:nth-child(even) .provenance-visual { order: 0; }
      .provenance-visual { min-height: 280px; }
      .provenance-copy { padding: 48px 24px; }
      .mission-inner { grid-template-columns: 1fr; gap: 40px; }
      .mission-visual { height: 300px; }
      .footer-top { grid-template-columns: 1fr 1fr; }
      .journal-grid { grid-template-columns: 1fr; }
      .product-layout { grid-template-columns: 1fr; }
      .product-img-wrap { position: static; aspect-ratio: 1; }
      .products-grid { grid-template-columns: 1fr 1fr; }
      .footer-bottom { flex-direction: column; gap: 16px; text-align: center; }
      .hero-ctas { flex-direction: column; align-items: center; }
      .form-row { grid-template-columns: 1fr; }
      .projects-grid { grid-template-columns: 1fr; }
      .contact-details { grid-template-columns: 1fr; }
    }
    @media (max-width: 600px) {
      .products-grid { grid-template-columns: 1fr; }
      .shop-grid { grid-template-columns: 1fr; }
    }
  `}</style>
);

// ── SVG Bottle Illustrations ──────────────────────────────────────────────────
const BottleSvg = ({ color = "#C2A46D", label = "", small = false }) => {
  const h = small ? 140 : 180;
  const w = small ? 56 : 72;
  return (
    <svg width={w} height={h} viewBox="0 0 72 180" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="30" y="0" width="12" height="20" rx="2" fill={color} opacity=".4"/>
      <rect x="28" y="18" width="16" height="10" rx="1" fill={color} opacity=".5"/>
      <path d="M24 28 C20 40 16 55 16 75 L16 155 C16 163 23 168 36 168 C49 168 56 163 56 155 L56 75 C56 55 52 40 48 28 Z" fill={color} opacity=".18"/>
      <path d="M24 28 C20 40 16 55 16 75 L16 155 C16 163 23 168 36 168 C49 168 56 163 56 155 L56 75 C56 55 52 40 48 28 Z" stroke={color} strokeWidth="1.5" fill="none" opacity=".6"/>
      <rect x="22" y="90" width="28" height="36" rx="1" fill={color} opacity=".08"/>
      <line x1="22" y1="90" x2="50" y2="90" stroke={color} strokeWidth=".5" opacity=".4"/>
      <line x1="22" y1="126" x2="50" y2="126" stroke={color} strokeWidth=".5" opacity=".4"/>
      <path d="M30 75 Q36 68 42 75" stroke={color} strokeWidth="1" fill="none" opacity=".3"/>
    </svg>
  );
};

const WildlifeIcon = ({ size = 80 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    <path d="M40 8 C40 8 28 18 22 30 C16 42 20 58 32 64 C38 67 46 65 50 60 C56 53 58 42 54 32 C50 22 44 12 40 8Z" stroke="#C2A46D" strokeWidth="1.2" fill="none" opacity=".4"/>
    <circle cx="32" cy="32" r="2.5" fill="#C2A46D" opacity=".5"/>
    <path d="M20 55 Q30 48 40 52 Q50 56 60 50" stroke="#C2A46D" strokeWidth="1" fill="none" opacity=".35"/>
    <path d="M15 35 Q22 30 30 35" stroke="#C2A46D" strokeWidth="1" fill="none" opacity=".3"/>
    <path d="M50 20 Q58 16 64 24 Q68 30 64 38" stroke="#C2A46D" strokeWidth="1" fill="none" opacity=".3"/>
  </svg>
);

const MountainIcon = ({ size = 120 }) => (
  <svg width={size} height={size} viewBox="0 0 120 80" fill="none">
    <path d="M0 80 L30 20 L60 50 L80 10 L120 80 Z" fill="none" stroke="#C2A46D" strokeWidth="1" opacity=".3"/>
    <path d="M0 80 L30 20 L60 50 L80 10 L120 80" fill="rgba(194,164,109,.05)"/>
    <circle cx="20" cy="15" r="8" fill="none" stroke="#C2A46D" strokeWidth=".8" opacity=".2"/>
    <line x1="10" y1="65" x2="110" y2="65" stroke="#C2A46D" strokeWidth=".5" opacity=".15"/>
  </svg>
);

const ForestIcon = ({ size = 100 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
    <path d="M50 5 L20 45 L32 45 L10 80 L90 80 L68 45 L80 45 Z" fill="none" stroke="#C2A46D" strokeWidth="1.2" opacity=".35"/>
    <rect x="44" y="80" width="12" height="15" fill="#C2A46D" opacity=".25"/>
  </svg>
);

// ── Products data ─────────────────────────────────────────────────────────────
const PRODUCTS = [
  { id: 1, name: "Session Lager", desc: "Clean, crisp, endlessly drinkable.", abv: "4.2% ABV", price: "£2.80", category: "lager", color: "#C2A46D", notes: ["Light malt sweetness", "Citrus hop finish", "Clean, dry finish"] },
  { id: 2, name: "Premium Lager", desc: "More depth. More body. More occasion.", abv: "5.0% ABV", price: "£3.20", category: "lager", color: "#B8934A", notes: ["Rich malt backbone", "Noble hop bitterness", "Long, warming finish"] },
  { id: 3, name: "Draught Stout", desc: "Dark, smooth and uncompromising.", abv: "4.6% ABV", price: "£3.40", category: "stout", color: "#6B6B6B", notes: ["Roasted coffee notes", "Dark chocolate body", "Velvety smooth texture"] },
  { id: 4, name: "Wild Berry Cider", desc: "Bright fruit. Clean finish. Distinctly Scottish.", abv: "4.0% ABV", price: "£2.90", category: "cider", color: "#8B3A6B", notes: ["Scottish blackcurrant", "Wild raspberry", "Bone dry finish"] },
  { id: 5, name: "Highland Botanical Gin", desc: "Botanical precision with a Highland backbone.", abv: "42% ABV", price: "£34.00", category: "gin", color: "#3A6B5B", notes: ["Juniper led", "Heather and thistle", "Long botanical finish"] },
  { id: 6, name: "Future Single Malt", desc: "A long-term ambition, laid down properly.", abv: "Coming Soon", price: "TBC", category: "whisky", color: "#8B6030", notes: ["Highland barley", "Scottish oak maturation", "In development"] },
];

// ── Navigation ────────────────────────────────────────────────────────────────
const Nav = ({ currentPage, setPage }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = ["Shop", "Our Story", "Mission", "Journal", "Stockists", "Trade"];
  const pageMap = { "Shop": "shop", "Our Story": "story", "Mission": "mission", "Journal": "journal", "Stockists": "contact", "Trade": "contact" };

  return (
    <nav className={`nav${scrolled ? " scrolled" : ""}`}>
      <div className="nav-wordmark" onClick={() => { setPage("home"); window.scrollTo(0,0); }}>Loch & Lynx</div>
      <ul className="nav-links">
        {links.map(l => (
          <li key={l}><a href={`/${l.toLowerCase().replace(' ','-')}`} onClick={e => { e.preventDefault(); setPage(pageMap[l]); window.scrollTo(0,0); }}>{l}</a></li>
        ))}
      </ul>
      <div className="nav-utils">
        <button className="nav-icon" title="Search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        </button>
        <button className="nav-icon" title="Account">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        </button>
        <button className="nav-icon" style={{ position: "relative" }} title="Cart">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
          <span className="nav-cart-badge">2</span>
        </button>
      </div>
    </nav>
  );
};

// ── Footer ────────────────────────────────────────────────────────────────────
const Footer = ({ setPage }) => (
  <footer className="footer">
    <div className="footer-top">
      <div>
        <div className="footer-brand-name">Loch & Lynx</div>
        <p className="footer-tagline">Premium Scottish drinks with a conservation mission. Every bottle funds the restoration of the natural world.</p>
      </div>
      <div>
        <p className="footer-col-title">Shop</p>
        <ul className="footer-links">
          {["All Products","Session Lager","Premium Lager","Draught Stout","Wild Berry Cider","Highland Botanical Gin"].map(i => (
            <li key={i}><a href="/" onClick={e=>{e.preventDefault();setPage("shop");window.scrollTo(0,0);}}>{i}</a></li>
          ))}
        </ul>
      </div>
      <div>
        <p className="footer-col-title">About</p>
        <ul className="footer-links">
          {[["Our Story","story"],["Mission","mission"],["Journal","journal"],["Stockists","contact"],["Trade","contact"],["Contact","contact"]].map(([l,p]) => (
            <li key={l}><a href="/" onClick={e=>{e.preventDefault();setPage(p);window.scrollTo(0,0);}}>{l}</a></li>
          ))}
        </ul>
      </div>
      <div>
        <p className="footer-col-title">Legal</p>
        <ul className="footer-links">
          {["Privacy Policy","Terms & Conditions","Shipping","Returns","Age Policy"].map(i=>(
            <li key={i}><a href="/">{i}</a></li>
          ))}
        </ul>
      </div>
    </div>
    <div className="footer-bottom">
      <p className="footer-copy">© 2025 Loch & Lynx Ltd. All rights reserved. Please drink responsibly.</p>
      <div className="footer-legal">
        <a href="/">Privacy</a>
        <a href="/">Terms</a>
        <a href="/">Instagram</a>
      </div>
    </div>
  </footer>
);

// ── Age Gate ──────────────────────────────────────────────────────────────────
const AgeGate = ({ onPass }) => {
  const [hidden, setHidden] = useState(false);
  const pass = () => { setHidden(true); setTimeout(onPass, 600); };
  return (
    <div className={`age-gate${hidden ? " hidden" : ""}`}>
      <div className="age-gate-inner">
        <div style={{ marginBottom: 24 }}><WildlifeIcon size={48}/></div>
        <p className="age-gate-title">Loch & Lynx</p>
        <p style={{ fontFamily: "var(--serif)", fontStyle: "italic", color: "var(--gold)", fontSize: 14, letterSpacing: ".1em", marginBottom: 24 }}>Scottish drinks for a wilder future.</p>
        <p className="age-gate-sub">By entering this site you confirm you are of legal drinking age in your country of residence.</p>
        <div className="age-gate-btns">
          <button className="btn-primary" onClick={pass}>Yes, I am of legal age</button>
          <button className="btn-outline" onClick={()=>{}}>No, take me back</button>
        </div>
      </div>
    </div>
  );
};

// ── Homepage ──────────────────────────────────────────────────────────────────
const Homepage = ({ setPage }) => (
  <div className="page">
    {/* HERO */}
    <section className="hero">
      <div className="hero-bg"/>
      <div className="hero-overlay"/>
      <div className="hero-mist"/>
      {/* Stars / grain effect */}
      <svg style={{ position:"absolute",inset:0,width:"100%",height:"100%",opacity:.06,pointerEvents:"none" }}>
        {[...Array(60)].map((_,i)=>(
          <circle key={i} cx={`${Math.sin(i*137.5)*50+50}%`} cy={`${Math.cos(i*97.3)*40+50}%`} r="1" fill="#F5F3EE"/>
        ))}
      </svg>
      {/* Mountain silhouette */}
      <div className="hero-mountains">
        <svg viewBox="0 0 1440 220" preserveAspectRatio="none" style={{ width:"100%",height:220,display:"block" }}>
          <path d="M0,220 L0,160 L120,80 L240,140 L360,60 L480,120 L600,40 L720,100 L840,30 L960,110 L1080,50 L1200,130 L1320,70 L1440,150 L1440,220 Z" fill="rgba(10,25,20,.7)"/>
          <path d="M0,220 L0,180 L180,130 L360,160 L540,110 L720,150 L900,100 L1080,145 L1260,115 L1440,160 L1440,220 Z" fill="rgba(5,15,12,.5)"/>
        </svg>
      </div>
      <div className="hero-content">
        <span className="hero-eyebrow">Est. Scotland</span>
        <h1 className="hero-title">Loch <em>&</em> Lynx</h1>
        <p className="hero-subtitle">Scottish drinks for a wilder future.</p>
        <p className="hero-sub2">A premium Scottish drinks company built to fund wildlife restoration.</p>
        <div className="hero-ctas">
          <button className="btn-primary" onClick={() => { setPage("shop"); window.scrollTo(0,0); }}>Explore the Collection</button>
          <button className="btn-outline" onClick={() => { setPage("mission"); window.scrollTo(0,0); }}>The Mission</button>
        </div>
      </div>
      <div className="hero-scroll"><span>Scroll</span><div className="scroll-line"/></div>
    </section>

    {/* STATEMENT */}
    <section className="statement">
      <div className="statement-inner">
        <span className="label">The Standard</span>
        <h2 className="h2">Classic drinks.<br/><em>Done exceptionally well.</em></h2>
        <div className="divider"/>
        <p className="body-copy">We are not here to reinvent what already works. We are here to make it properly — with Scottish provenance, obsessive standards, and a conservation mission behind every bottle.</p>
      </div>
    </section>

    {/* PRODUCTS */}
    <section className="products-section">
      <div style={{ textAlign: "center", maxWidth: 600, margin: "0 auto" }}>
        <span className="label" style={{ color: "var(--gold)" }}>The Range</span>
        <h2 className="h2 h2-light">Six drinks.<br/><em>One standard.</em></h2>
      </div>
      <div className="products-grid">
        {PRODUCTS.map(p => (
          <div className="product-card" key={p.id} onClick={() => { setPage("product"); window.scrollTo(0,0); }}>
            <div className="product-bottle"><BottleSvg color={p.color}/></div>
            <p className="product-abv">{p.abv}</p>
            <p className="product-name">{p.name}</p>
            <p className="product-desc">{p.desc}</p>
            <a className="product-link" href="/product" onClick={e=>{e.preventDefault();setPage("product");window.scrollTo(0,0);}}>View Product</a>
          </div>
        ))}
      </div>
      <div style={{ textAlign: "center", marginTop: 60 }}>
        <button className="btn-outline" onClick={() => { setPage("shop"); window.scrollTo(0,0); }}>Shop the Range</button>
      </div>
    </section>

    {/* PROVENANCE */}
    <section className="provenance">
      {[
        { title: "From Scotland.\nNot just inspired by it.", body: "Loch & Lynx is being built around a simple principle: if we are going to represent Scotland, the product should come from Scotland in every way that matters.", icon: <MountainIcon size={160}/> },
        { title: "Barley, water,\nbotanicals, oak.", body: "Every ingredient and production decision pushes toward provenance, quality and long-term integrity. The land shapes the drink. The drink funds the land.", icon: <svg width={100} height={100} viewBox="0 0 100 100" fill="none"><path d="M50 10 C50 10 20 30 20 60 C20 80 34 90 50 90 C66 90 80 80 80 60 C80 30 50 10 50 10Z" stroke="#C2A46D" strokeWidth="1.2" fill="none" opacity=".4"/><path d="M50 35 L50 85" stroke="#C2A46D" strokeWidth="1" opacity=".3"/><path d="M35 55 Q42 48 50 55 Q58 62 65 55" stroke="#C2A46D" strokeWidth="1" fill="none" opacity=".3"/></svg> },
        { title: "Craft without\ncostume.", body: "We respect heritage. We do not cosplay it. Loch & Lynx is a modern premium brand built with old-world patience and contemporary standards.", icon: <svg width={100} height={80} viewBox="0 0 100 80" fill="none"><rect x="10" y="20" width="80" height="45" rx="4" stroke="#C2A46D" strokeWidth="1.2" fill="none" opacity=".4"/><path d="M30 20 L30 10 Q50 5 70 10 L70 20" stroke="#C2A46D" strokeWidth="1" fill="none" opacity=".3"/><line x1="10" y1="38" x2="90" y2="38" stroke="#C2A46D" strokeWidth=".8" opacity=".2"/></svg> },
      ].map((p, i) => (
        <div className="provenance-panel" key={i}>
          <div className="provenance-visual">
            <div style={{ position:"absolute",inset:0,background:`linear-gradient(${135+i*30}deg, rgba(15,42,36,.95) 0%, rgba(${10+i*5},${30+i*8},${24+i*6},.85) 100%)` }}/>
            <div style={{ position:"relative" }}>{p.icon}</div>
          </div>
          <div className="provenance-copy">
            <span className="label">Scottish by Design</span>
            <h2 className="h2" style={{ whiteSpace:"pre-line", marginBottom:20 }}>{p.title}</h2>
            <p className="body-copy" style={{ margin:0, textAlign:"left" }}>{p.body}</p>
          </div>
        </div>
      ))}
    </section>

    {/* MISSION BLOCK */}
    <section className="mission-section">
      <div className="mission-inner">
        <div>
          <span className="label">The Wildlife Fund</span>
          <h2 className="h2 h2-light" style={{ marginBottom:24 }}>A drinks company<br/><em>funding nature.</em></h2>
          <p className="body-copy body-copy-light" style={{ margin:"0 0 28px", textAlign:"left" }}>Scotland is one of the most nature-depleted countries on Earth. Loch & Lynx exists to build something commercially serious that helps reverse that.</p>
          <p className="body-copy body-copy-light" style={{ margin:"0 0 40px", textAlign:"left" }}>A portion of what we sell helps fund wildlife restoration through the Loch & Lynx Wildlife Fund.</p>
          <button className="btn-primary" onClick={() => { setPage("mission"); window.scrollTo(0,0); }}>Discover the Mission</button>
          <div className="mission-stats">
            {[["100%","Scottish sourcing focus"],["Every bottle","Funds the wildlife fund"],["Long-term","Conservation ambition"],["1 planet","Worth protecting"]].map(([n,l]) => (
              <div className="mission-stat" key={l}>
                <div className="mission-stat-num">{n}</div>
                <div className="mission-stat-label">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="mission-visual">
          <div style={{ position:"absolute",inset:0,background:"linear-gradient(160deg,rgba(10,25,20,.9),rgba(5,15,12,.7))" }}/>
          <div style={{ position:"relative",textAlign:"center" }}>
            <WildlifeIcon size={120}/>
            <p style={{ fontFamily:"var(--serif)",fontStyle:"italic",color:"rgba(194,164,109,.5)",fontSize:13,marginTop:16,letterSpacing:".08em" }}>Loch & Lynx Wildlife Fund</p>
          </div>
        </div>
      </div>
    </section>

    {/* LEGACY */}
    <section className="legacy">
      <div className="legacy-inner">
        <span className="label">The Long Game</span>
        <h2 className="h2 h2-light">Built for<br/><em>the long term.</em></h2>
        <div className="legacy-lines">
          <span className="legacy-line">Not for noise.</span>
          <span className="legacy-line">Not for quick exits.</span>
          <span className="legacy-line">Not for short-term hype.</span>
        </div>
        <p className="legacy-statement">Loch & Lynx is being built slowly, properly, and with the ambition to become a world-class Scottish drinks house with a meaningful conservation legacy behind it.</p>
      </div>
    </section>

    {/* JOURNAL */}
    <section className="journal-section">
      <div style={{ textAlign:"center" }}>
        <span className="label">Field Notes</span>
        <h2 className="h2">From the wild.</h2>
      </div>
      <div className="journal-grid">
        {[
          { cat:"Conservation", title:"Why Scotland Needs Rewilding", excerpt:"Once home to wolves, lynx and vast ancient forests, Scotland is now one of Europe's most nature-depleted landscapes. Here is why that needs to change." },
          { cat:"Supply Chain", title:"Building a Truly Scottish Supply Chain", excerpt:"From Highland barley to botanical harvest, we are tracing every ingredient back to its Scottish source — and what we are learning along the way." },
          { cat:"Field Notes", title:"Field Notes from the Wild", excerpt:"Observations, encounters and thinking from the landscapes that shape what we make. The land is the brief. The drink is the response." },
        ].map((a, i) => (
          <div className="journal-card" key={i}>
            <div className="journal-card-img">
              <div className="journal-card-img-inner" style={{ background:`linear-gradient(${140+i*25}deg,rgba(15,42,36,.95),rgba(${10+i*8},${25+i*10},${18+i*8},.9))` }}>
                {i === 0 ? <ForestIcon size={80}/> : i === 1 ? <svg width={80} height={80} viewBox="0 0 80 80" fill="none"><path d="M10 70 Q20 20 40 15 Q60 10 70 70" stroke="#C2A46D" strokeWidth="1.2" fill="rgba(194,164,109,.06)" opacity=".5"/><line x1="40" y1="15" x2="40" y2="70" stroke="#C2A46D" strokeWidth=".8" opacity=".25"/></svg> : <MountainIcon size={100}/>}
              </div>
            </div>
            <div className="journal-card-body">
              <span className="journal-cat">{a.cat}</span>
              <p className="journal-title">{a.title}</p>
              <p className="journal-excerpt">{a.excerpt}</p>
            </div>
          </div>
        ))}
      </div>
    </section>

    {/* NEWSLETTER */}
    <section className="newsletter">
      <div className="newsletter-inner">
        <span className="label">Stay Close</span>
        <h2 className="h2 h2-light"><em>Join the Pack</em></h2>
        <div className="divider"/>
        <p className="body-copy body-copy-light">Early releases. Field notes. Conservation updates.</p>
        <div className="newsletter-form" style={{ marginTop:36 }}>
          <input className="newsletter-input" type="email" placeholder="Your email address"/>
          <button className="newsletter-btn">Sign Up</button>
        </div>
        <p style={{ fontFamily:"var(--sans)",fontSize:10,color:"rgba(245,243,238,.25)",marginTop:16,letterSpacing:".1em" }}>No noise. Unsubscribe any time.</p>
      </div>
    </section>

    <Footer setPage={setPage}/>
  </div>
);

// ── Shop Page ─────────────────────────────────────────────────────────────────
const ShopPage = ({ setPage }) => {
  const [activeFilter, setActiveFilter] = useState("all");
  const filters = ["all","lager","stout","cider","gin","whisky"];
  const filtered = activeFilter === "all" ? PRODUCTS : PRODUCTS.filter(p => p.category === activeFilter);

  return (
    <div className="page">
      <div className="shop-hero">
        <span className="label" style={{ color:"var(--gold)" }}>The Collection</span>
        <h1 className="h2 h2-light" style={{ marginTop:8 }}>Shop the Range</h1>
        <p style={{ fontFamily:"var(--serif)",fontStyle:"italic",color:"rgba(245,243,238,.45)",marginTop:12,fontSize:16 }}>Premium Scottish drinks with a conservation mission.</p>
      </div>
      <div style={{ padding:"0 48px", background:"var(--offwhite)" }}>
        <div className="shop-filters">
          {filters.map(f => (
            <button key={f} className={`filter-btn${activeFilter===f?" active":""}`} onClick={()=>setActiveFilter(f)}>
              {f.charAt(0).toUpperCase()+f.slice(1)}
            </button>
          ))}
        </div>
        <div className="shop-grid" style={{ paddingBottom:80 }}>
          {filtered.map(p => (
            <div className="shop-card" key={p.id} onClick={()=>{setPage("product");window.scrollTo(0,0);}}>
              <div className="shop-card-img">
                <BottleSvg color={p.color} small={false}/>
              </div>
              <p className="shop-card-name">{p.name}</p>
              <p className="shop-card-desc">{p.abv} · {p.desc}</p>
              <div className="shop-card-row">
                <span className="shop-card-price">{p.price}</span>
                <button className="btn-add" onClick={e=>{e.stopPropagation();}}>Add to Cart</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer setPage={setPage}/>
    </div>
  );
};

// ── Product Page ──────────────────────────────────────────────────────────────
const ProductPage = ({ setPage }) => {
  const p = PRODUCTS[4]; // Highland Botanical Gin as example
  return (
    <div className="page" style={{ background:"var(--offwhite)", paddingTop:80 }}>
      <div className="product-page">
        <div style={{ marginBottom:20 }}>
          <a href="/shop" style={{ fontFamily:"var(--sans)",fontSize:11,letterSpacing:".15em",textTransform:"uppercase",color:"rgba(17,17,17,.4)",textDecoration:"none" }} onClick={e=>{e.preventDefault();setPage("shop");window.scrollTo(0,0);}}>← Back to Shop</a>
        </div>
        <div className="product-layout">
          <div className="product-img-wrap">
            <BottleSvg color={p.color} small={false}/>
          </div>
          <div className="product-info">
            <span className="label">{p.category.charAt(0).toUpperCase()+p.category.slice(1)}</span>
            <h1 className="product-page-name">{p.name}</h1>
            <p className="product-page-abv">{p.abv}</p>
            <p className="product-page-price">{p.price}</p>
            <button className="btn-atc">Add to Cart</button>

            <div className="product-section">
              <p className="product-section-title">Description</p>
              <p className="product-section-body">A botanical gin crafted using juniper, heather and native Scottish botanicals, distilled with soft Highland water. Precision-made. Distinctly Scottish.</p>
            </div>
            <div className="product-section">
              <p className="product-section-title">Tasting Notes</p>
              <div className="tasting-notes">
                {p.notes.map(n => <div className="tasting-note" key={n}>{n}</div>)}
              </div>
            </div>
            <div className="product-section">
              <p className="product-section-title">Scottish Craft</p>
              <p className="product-section-body">Brewed and distilled in Scotland using a fully Scottish supply chain wherever possible. From barley field to bottle, every step is considered.</p>
            </div>
            <div className="product-section">
              <p className="product-section-title">Conservation</p>
              <p className="product-section-body">Every bottle contributes to the Loch & Lynx Wildlife Fund, supporting rewilding and habitat restoration across Scotland.</p>
            </div>
          </div>
        </div>

        <div style={{ marginTop:100, borderTop:"1px solid rgba(17,17,17,.08)", paddingTop:64 }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <span className="label">You May Also Like</span>
            <h3 className="h2" style={{ fontSize:32 }}>From the Range</h3>
          </div>
          <div className="products-grid" style={{ maxWidth:"100%" }}>
            {PRODUCTS.filter(x=>x.id!==p.id).slice(0,3).map(rp=>(
              <div className="product-card" key={rp.id} onClick={()=>window.scrollTo(0,0)}>
                <div className="product-bottle"><BottleSvg color={rp.color}/></div>
                <p className="product-abv">{rp.abv}</p>
                <p className="product-name">{rp.name}</p>
                <p className="product-desc">{rp.desc}</p>
                <a className="product-link" href="/product">View Product</a>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer setPage={setPage}/>
    </div>
  );
};

// ── Mission Page ──────────────────────────────────────────────────────────────
const MissionPage = ({ setPage }) => (
  <div className="page">
    <div className="mission-hero">
      <div style={{ position:"absolute",inset:0,background:"linear-gradient(160deg,rgba(5,18,14,.95),rgba(15,42,24,.9))" }}/>
      <div style={{ position:"relative",zIndex:2,maxWidth:700,margin:"0 auto",textAlign:"center" }}>
        <span className="label">Our Purpose</span>
        <h1 className="h2 h2-light" style={{ marginBottom:24 }}>A drinks company<br/><em>funding nature.</em></h1>
        <div className="divider"/>
        <p className="body-copy body-copy-light">Scotland has lost more biodiversity than almost any country on Earth. Loch & Lynx exists to help change that through commerce done properly.</p>
      </div>
    </div>

    <div className="mission-body">
      {[
        {
          label:"The Problem",
          title:"Scotland's lost wildness.",
          body:"Scotland once supported wolves, lynx, wild boar and vast ancient temperate forests. Today it ranks as one of the most nature-depleted countries on Earth. The causes are complex — centuries of land use change, intensive agriculture, and the slow erosion of native habitat. The result is a landscape that has lost its ecological heart.",
        },
        {
          label:"Our Model",
          title:"How the Wildlife Fund works.",
          body:"A portion of every unit sold contributes to the Loch & Lynx Wildlife Fund. The fund is designed to grow as the company grows — meaning the more we sell, the more conservation we can fund. It is not a charity add-on. It is built into the commercial model from day one.",
        },
        {
          label:"The Vision",
          title:"A long-term ambition.",
          body:"Our long-term ambition is to build one of the largest private conservation funds in Europe — funded not by donors, but by the commercial success of a world-class Scottish drinks company. We are building this slowly, properly, and for the long term.",
        },
      ].map((b, i) => (
        <div className="mission-block" key={i}>
          <span className="label">{b.label}</span>
          <h2 className="h2"><em>{b.title}</em></h2>
          <div className="divider"/>
          <p className="body-copy">{b.body}</p>
        </div>
      ))}

      <div style={{ maxWidth:900, margin:"0 auto", padding:"80px 48px" }}>
        <div style={{ textAlign:"center", marginBottom:48 }}>
          <span className="label">What We Support</span>
          <h2 className="h2"><em>Conservation in action.</em></h2>
        </div>
        <div className="projects-grid">
          {[
            { name:"Rewilding Scotland", desc:"Supporting the reintroduction of native species and the restoration of ancient woodland across the Highlands and beyond." },
            { name:"Habitat Restoration", desc:"Funding the recovery of peatland, native forest and freshwater ecosystems that form the ecological backbone of Scotland." },
            { name:"Species Recovery", desc:"Partnering with organisations working to bring back wolves, lynx, white-tailed eagles and other lost species to Scottish landscapes." },
            { name:"Global Partnerships", desc:"Building relationships with leading conservation organisations to ensure the Wildlife Fund is guided by science and long-term ecological thinking." },
          ].map(p => (
            <div className="project-card" key={p.name}>
              <p className="project-name">{p.name}</p>
              <p className="project-desc">{p.desc}</p>
            </div>
          ))}
        </div>
        <div style={{ textAlign:"center", marginTop:60 }}>
          <button className="btn-primary" onClick={()=>{setPage("shop");window.scrollTo(0,0);}}>Shop & Support the Fund</button>
        </div>
      </div>
    </div>
    <section className="newsletter">
      <div className="newsletter-inner">
        <span className="label">Stay Informed</span>
        <h2 className="h2 h2-light"><em>Conservation updates</em></h2>
        <div className="divider"/>
        <p className="body-copy body-copy-light">Field notes, wildlife fund updates and the ongoing build-out of Loch & Lynx.</p>
        <div className="newsletter-form" style={{ marginTop:36 }}>
          <input className="newsletter-input" type="email" placeholder="Your email address"/>
          <button className="newsletter-btn">Sign Up</button>
        </div>
      </div>
    </section>
    <Footer setPage={setPage}/>
  </div>
);

// ── Contact Page ──────────────────────────────────────────────────────────────
const ContactPage = ({ setPage }) => (
  <div className="page" style={{ background:"var(--offwhite)", paddingTop:80 }}>
    <div className="contact-page">
      <span className="label">Get in Touch</span>
      <h1 className="h2" style={{ marginBottom:16 }}>Work with us.</h1>
      <p className="body-copy" style={{ margin:0, textAlign:"left" }}>Loch & Lynx is selectively partnering with bars, restaurants, retailers and conservation partners.</p>

      <div className="contact-form">
        <div className="form-row">
          <div className="form-field">
            <label className="form-label">First Name</label>
            <input className="form-input" type="text" placeholder="James"/>
          </div>
          <div className="form-field">
            <label className="form-label">Last Name</label>
            <input className="form-input" type="text" placeholder="Mackenzie"/>
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">Email</label>
          <input className="form-input" type="email" placeholder="james@establishment.co.uk"/>
        </div>
        <div className="form-field">
          <label className="form-label">Business / Organisation</label>
          <input className="form-input" type="text" placeholder="Your bar, restaurant or organisation"/>
        </div>
        <div className="form-field">
          <label className="form-label">Enquiry Type</label>
          <input className="form-input" type="text" placeholder="Trade · Press · Conservation · General"/>
        </div>
        <div className="form-field">
          <label className="form-label">Message</label>
          <textarea className="form-textarea" placeholder="Tell us about your interest in Loch & Lynx..."/>
        </div>
        <button className="form-submit">Send Enquiry</button>
      </div>

      <div className="contact-details">
        {[
          { title:"General Enquiries", val:"hello@lochandlynx.com" },
          { title:"Trade & Wholesale", val:"trade@lochandlynx.com" },
          { title:"Press & Partnerships", val:"press@lochandlynx.com" },
        ].map(d => (
          <div className="contact-detail" key={d.title}>
            <p className="contact-detail-title">{d.title}</p>
            <p className="contact-detail-val">{d.val}</p>
          </div>
        ))}
      </div>
    </div>
    <Footer setPage={setPage}/>
  </div>
);

// ── Our Story Page ────────────────────────────────────────────────────────────
const StoryPage = ({ setPage }) => (
  <div className="page">
    <div className="mission-hero">
      <div style={{ position:"absolute",inset:0,background:"linear-gradient(160deg,rgba(5,18,14,.95),rgba(15,42,24,.9))" }}/>
      <div style={{ position:"relative",zIndex:2,maxWidth:680,margin:"0 auto",textAlign:"center" }}>
        <span className="label">Our Story</span>
        <h1 className="h2 h2-light" style={{ marginBottom:24 }}><em>Why we are doing this.</em></h1>
        <div className="divider"/>
        <p className="body-copy body-copy-light">Loch & Lynx was born from a belief that business should do more than generate revenue. It should build something worth leaving behind.</p>
      </div>
    </div>
    <div className="mission-body">
      {[
        { label:"The Beginning", title:"Scotland once had wolves.", body:"Scotland was once home to some of the most extraordinary wildlife in Europe. Wolves, lynx, wild boar, and ancient temperate rainforests that stretched from coast to coast. Those things are mostly gone. What remains is a stripped landscape that is quietly accepted as normal. We do not accept that." },
        { label:"The Idea", title:"A different type of drinks company.", body:"Loch & Lynx exists to build something unusual — a premium drinks company designed to fund the restoration of the natural world. Not as a side project. Not as a marketing angle. As the core commercial engine of the business." },
        { label:"The Build", title:"Starting small. Building carefully.", body:"We are starting with a core range of classic Scottish drinks: lager, stout, cider, gin, and a single malt whisky currently being laid down. Every product is built around Scottish provenance, obsessive quality standards, and the ambition to create something genuinely world-class." },
        { label:"The Ambition", title:"For the long term.", body:"The goal is not to build a drinks company that talks about conservation. The goal is to build a drinks company strong enough — and commercially serious enough — that conservation can be funded properly, at scale, for generations. That takes time. We are in no hurry." },
      ].map((b, i) => (
        <div className="mission-block" key={i}>
          <span className="label">{b.label}</span>
          <h2 className="h2"><em>{b.title}</em></h2>
          <div className="divider"/>
          <p className="body-copy">{b.body}</p>
        </div>
      ))}
    </div>
    <section className="legacy">
      <div className="legacy-inner">
        <span className="label">The Standard</span>
        <h2 className="h2 h2-light">A £100m brand.<br/><em>A legacy worth building.</em></h2>
        <div style={{ marginTop:32 }}>
          <button className="btn-primary" onClick={()=>{setPage("mission");window.scrollTo(0,0);}}>Read the Mission</button>
        </div>
      </div>
    </section>
    <Footer setPage={setPage}/>
  </div>
);

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("home");
  const [ageVerified, setAgeVerified] = useState(false);

  const pages = {
    home: <Homepage setPage={setPage}/>,
    shop: <ShopPage setPage={setPage}/>,
    product: <ProductPage setPage={setPage}/>,
    mission: <MissionPage setPage={setPage}/>,
    story: <StoryPage setPage={setPage}/>,
    contact: <ContactPage setPage={setPage}/>,
  };

  return (
    <>
      <FontLoader/>
      <GlobalStyles/>
      {!ageVerified && <AgeGate onPass={() => setAgeVerified(true)}/>}
      <Nav currentPage={page} setPage={setPage}/>
      <main>{pages[page] || pages.home}</main>
    </>
  );
}
